import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "./motion";
import { api } from "../lib/api";
import { BEST_FOR } from "../data/bestFor";
import { RANKINGS } from "../data/rankings";
import { LEARN } from "../data/learn";

const Group = ({ title, links }) => (
  <div>
    <h3 className="mb-4 font-display text-xs font-bold uppercase tracking-[0.2em] text-[#4687FF]">{title}</h3>
    <ul className="space-y-3">
      {links.map((l) => (
        <li key={l.to}>
          <Link
            to={l.to}
            className="group inline-flex items-start gap-1.5 text-sm font-semibold leading-snug text-slate-600 transition-colors duration-150 hover:text-[#4687FF]"
          >
            <ArrowRight size={14} className="mt-0.5 shrink-0 text-slate-300 transition-colors duration-150 group-hover:text-[#4687FF]" />
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export const RelatedGuides = ({ vpn, excludeBestFor, excludeRanking }) => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/posts?limit=4").then((r) => setPosts(r.data.slice(0, 4))).catch(() => {});
  }, []);

  const pickRelevant = (list, getOrder, mapFn) => {
    for (const depth of [1, 2, 4]) {
      const hits = list.filter((item) => {
        const idx = getOrder(item).indexOf(vpn.slug);
        return idx >= 0 && idx <= depth;
      });
      if (hits.length >= 3) return hits.slice(0, 4).map(mapFn);
    }
    return list.slice(0, 4).map(mapFn);
  };

  const bestFor = pickRelevant(
    BEST_FOR.filter((b) => b.slug !== excludeBestFor),
    (b) => b.order,
    (b) => ({
      label: `Best VPN for ${b.label}`,
      to: `/best-vpn-for/${b.slug}`,
    })
  );

  const rankings = pickRelevant(
    RANKINGS.filter((r) => r.slug !== excludeRanking),
    (r) => r.order,
    (r) => ({
      label: r.title.replace(": Rankings", "").replace(" Rankings", ""),
      to: `/rankings/${r.slug}`,
    })
  );

  const learn = LEARN.slice(0, 4).map((l) => ({ label: l.title, to: `/learn/${l.slug}` }));

  const blog = posts.map((p) => ({ label: p.title, to: `/blog/${p.slug}` }));

  return (
    <Reveal>
      <section
        data-testid="related-guides"
        className="mt-16 border border-slate-200 bg-slate-50 p-8 sm:p-10"
      >
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#4687FF]">Keep Exploring</p>
            <h2 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-[#0F172A]">
              Related Guides & Rankings
            </h2>
          </div>
          <Link to="/best-vpns" className="font-display text-sm font-bold text-[#4687FF] hover:text-[#2563eb]">
            All rankings →
          </Link>
        </div>
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {bestFor.length > 0 && <Group title={`${vpn.name} Use Cases`} links={bestFor} />}
          {rankings.length > 0 && <Group title="Top Rankings" links={rankings} />}
          <Group title="Learning Center" links={learn} />
          {blog.length > 0 && <Group title="From the Blog" links={blog} />}
        </div>
      </section>
    </Reveal>
  );
};
